/**
 * AssignmentsPage — Phrase-style Linguist Portal (I1)
 *
 * Dedicated inbox for translators and reviewers to manage their assigned jobs.
 * Four buckets matching Phrase: New Work | Accepted | Completed | Delivered/Declined
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayoutApple } from '@/components/apple-ui';
import {
  Briefcase, Clock, PlayCircle, CheckCircle2, PackageCheck, XCircle,
  Calendar, FileText, Loader2, RefreshCw, ExternalLink, ChevronRight,
  AlertTriangle, Inbox
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'new',       label: 'New Work',  icon: Clock,         statuses: ['new'] },
  { id: 'accepted',  label: 'Accepted',  icon: PlayCircle,    statuses: ['accepted', 'in_progress'] },
  { id: 'completed', label: 'Completed', icon: CheckCircle2,  statuses: ['completed'] },
  { id: 'archived',  label: 'Closed',    icon: PackageCheck,  statuses: ['delivered', 'declined'] },
];

const STATUS_CFG = {
  new:         { label: 'New',         cls: 'bg-blue-50 text-blue-700 border-blue-100',    dot: 'bg-blue-500' },
  accepted:    { label: 'Accepted',    cls: 'bg-indigo-50 text-indigo-700 border-indigo-100', dot: 'bg-indigo-500' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-50 text-amber-700 border-amber-100', dot: 'bg-amber-500' },
  completed:   { label: 'Completed',   cls: 'bg-green-50 text-green-700 border-green-100', dot: 'bg-green-500' },
  delivered:   { label: 'Delivered',   cls: 'bg-purple-50 text-purple-700 border-purple-100', dot: 'bg-purple-500' },
  declined:    { label: 'Declined',    cls: 'bg-red-50 text-red-700 border-red-100',       dot: 'bg-red-400' },
};

function deadlineInfo(deadline) {
  if (!deadline) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const due = new Date(deadline);
  const days = Math.round((due - today) / 86400000);
  if (days < 0)  return { text: `${Math.abs(days)}d overdue`, cls: 'text-red-600 font-semibold', urgent: true };
  if (days === 0) return { text: 'Due today',   cls: 'text-amber-600 font-semibold', urgent: true };
  if (days <= 2)  return { text: `${days}d left`, cls: 'text-amber-500 font-medium', urgent: false };
  return {
    text: due.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
    cls: 'text-gray-500', urgent: false
  };
}

function JobCard({ job, onAction, onOpenCAT, isDarkMode }) {
  const [busy, setBusy] = useState(false);
  const dl = deadlineInfo(job.deadline);
  const statusCfg = STATUS_CFG[job.status] || STATUS_CFG.new;

  const action = async (act) => {
    setBusy(true);
    await onAction(job.job_id, act);
    setBusy(false);
  };

  return (
    <div className={`rounded-2xl border p-5 transition-all hover:shadow-md ${
      dl?.urgent ? 'border-amber-200' : (isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white')
    }`}>
      {/* Top row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${statusCfg.cls}`}>
            {statusCfg.label}
          </span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
            job.workflow_step === 1
              ? 'bg-blue-50 text-blue-700'
              : 'bg-orange-50 text-orange-700'
          }`}>{job.step_name}</span>
        </div>
        {dl && (
          <span className={`flex items-center gap-1 text-xs flex-shrink-0 ${dl.cls}`}>
            {dl.urgent && <AlertTriangle size={11} />}
            <Calendar size={11} /> {dl.text}
          </span>
        )}
      </div>

      {/* File + project */}
      <div className="mb-3">
        <p className={`font-semibold text-sm truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{job.file_name}</p>
        {job.project_name && (
          <p className={`text-xs mt-0.5 truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{job.project_name}</p>
        )}
        {job.notes && (
          <p className={`text-xs mt-1 italic truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{job.notes}</p>
        )}
      </div>

      {/* Stats row */}
      <div className={`flex items-center gap-4 text-xs mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {job.word_count > 0 && (
          <span className="flex items-center gap-1">
            <FileText size={11} /> {job.word_count.toLocaleString()} words
          </span>
        )}
        {job.segment_count > 0 && (
          <span>{job.segment_count} segments</span>
        )}
        {job.accepted_at && (
          <span>Accepted {new Date(job.accepted_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</span>
        )}
        {job.completed_at && (
          <span>Done {new Date(job.completed_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* New: accept / decline */}
        {job.status === 'new' && (
          <>
            <button
              onClick={() => action('accept')}
              disabled={busy}
              className="flex-1 py-1.5 text-sm font-semibold bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin mx-auto" /> : 'Accept'}
            </button>
            <button
              onClick={() => action('decline')}
              disabled={busy}
              className="flex-1 py-1.5 text-sm font-medium bg-red-50 hover:bg-red-100 text-red-700 rounded-xl transition-colors disabled:opacity-50"
            >
              Decline
            </button>
          </>
        )}
        {/* Accepted/In-progress: open CAT + complete */}
        {(job.status === 'accepted' || job.status === 'in_progress') && (
          <>
            {job.project_id && (
              <button
                onClick={() => onOpenCAT(job.project_id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-xl transition-colors ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                <ExternalLink size={13} /> Open CAT
              </button>
            )}
            <button
              onClick={() => action('complete')}
              disabled={busy}
              className="flex-1 py-1.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <><CheckCircle2 size={14} /> Mark Complete</>}
            </button>
          </>
        )}
        {/* Completed: open CAT (read) */}
        {job.status === 'completed' && job.project_id && (
          <button
            onClick={() => onOpenCAT(job.project_id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-xl transition-colors ${
              isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <ExternalLink size={13} /> View in CAT
          </button>
        )}
      </div>
    </div>
  );
}

export default function AssignmentsPage({ user, planInfo, onLogout }) {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('new');
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const token = localStorage.getItem('token');

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`${API_URL}/api/jobs`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setAllJobs(await res.json());
      else setLoadError('Failed to load assignments. Please try again.');
    } catch (e) { setLoadError('Network error. Please check your connection.'); }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const handleAction = async (jobId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/jobs/${jobId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const d = await res.json(); toast.error(d.detail || 'Action failed'); return; }
      await loadJobs();
      toast.success(`Job ${action}ed`);
    } catch (e) { toast.error('Action failed'); }
  };

  const handleOpenCAT = (projectId) => {
    navigate(`/cat?project=${projectId}`);
  };

  const currentStatuses = TABS.find(t => t.id === activeTab)?.statuses || [];
  const visibleJobs = allJobs.filter(j => currentStatuses.includes(j.status));

  // Tab counts
  const tabCounts = TABS.reduce((acc, tab) => {
    acc[tab.id] = allJobs.filter(j => tab.statuses.includes(j.status)).length;
    return acc;
  }, {});

  // Summary stats
  const activeWords = allJobs
    .filter(j => j.status === 'accepted' || j.status === 'in_progress')
    .reduce((s, j) => s + (j.word_count || 0), 0);
  const overdueCount = allJobs.filter(j => {
    if (!j.deadline || ['completed','delivered','declined'].includes(j.status)) return false;
    return new Date(j.deadline) < new Date();
  }).length;

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      <div className="apple-content">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="apple-content-title flex items-center gap-3">
              <Briefcase size={28} className="text-blue-600" />
              My Assignments
            </h1>
            <p className="apple-content-subtitle">Jobs assigned to you across all projects</p>
          </div>
          <button
            onClick={loadJobs}
            className={`p-2 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Summary bar */}
        {allJobs.length > 0 && (
          <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6`}>
            <div className={`rounded-2xl p-4 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>New Work</p>
              <p className={`text-2xl font-bold ${tabCounts.new > 0 ? 'text-blue-600' : (isDarkMode ? 'text-gray-300' : 'text-gray-900')}`}>{tabCounts.new}</p>
            </div>
            <div className={`rounded-2xl p-4 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>In Progress</p>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{tabCounts.accepted}</p>
            </div>
            <div className={`rounded-2xl p-4 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active Words</p>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{activeWords.toLocaleString()}</p>
            </div>
            <div className={`rounded-2xl p-4 border ${overdueCount > 0 ? 'bg-red-50 border-red-200' : (isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200')}`}>
              <p className={`text-xs font-medium mb-1 ${overdueCount > 0 ? 'text-red-500' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>Overdue</p>
              <p className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-600' : (isDarkMode ? 'text-gray-300' : 'text-gray-900')}`}>{overdueCount}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className={`flex gap-1 p-1 rounded-2xl mb-6 w-fit ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const count = tabCounts[tab.id];
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? (isDarkMode ? 'bg-gray-700 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm')
                    : (isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900')
                }`}
              >
                <Icon size={15} />
                {tab.label}
                {count > 0 && (
                  <span className={`px-1.5 py-0.5 text-xs font-bold rounded-full min-w-[20px] text-center ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : (isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700')
                  }`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Error */}
        {!loading && loadError && (
          <div className={`flex flex-col items-center justify-center py-20 gap-3 text-center rounded-2xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <AlertTriangle size={48} className="text-amber-500" />
            <p className={`text-base font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{loadError}</p>
            <button
              onClick={loadJobs}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors"
            >
              <RefreshCw size={15} /> Try Again
            </button>
          </div>
        )}

        {/* Content */}
        {loadError ? null : loading ? (
          <div className="flex items-center justify-center py-20 gap-3">
            <Loader2 size={24} className="animate-spin text-blue-500" />
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading assignments...</span>
          </div>
        ) : visibleJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
            <Inbox size={48} className={isDarkMode ? 'text-gray-600' : 'text-gray-300'} />
            <p className={`text-base font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {activeTab === 'new' ? 'No new assignments' : `No ${TABS.find(t=>t.id===activeTab)?.label.toLowerCase()} jobs`}
            </p>
            {activeTab === 'new' && (
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Your team manager will assign jobs here when ready
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleJobs.map(job => (
              <JobCard
                key={job.job_id}
                job={job}
                onAction={handleAction}
                onOpenCAT={handleOpenCAT}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        )}
      </div>
    </MainLayoutApple>
  );
}
